import { useMemo } from 'react';
import { Layers, ArrowRight, Server, Shield, Zap, AlertTriangle } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface ProtocolInsight {
  type: 'info' | 'warning' | 'success';
  icon: React.ReactNode;
  title: string;
  description: string;
  entries?: HAREntry[];
}

interface ProtocolStats {
  protocol: string;
  version: string;
  count: number;
  totalSize: number;
  avgTime: number;
  features: string[];
}

export function HTTP2HTTP3Analysis() {
  const { filteredEntries, selectEntry } = useHARStore();

  const protocolStats = useMemo(() => {
    const stats: Record<string, ProtocolStats> = {};

    filteredEntries.forEach(entry => {
      const httpVersion = entry.request.httpVersion?.toLowerCase() || 'http/1.1';
      const key = httpVersion;

      if (!stats[key]) {
        stats[key] = {
          protocol: httpVersion,
          version: httpVersion,
          count: 0,
          totalSize: 0,
          avgTime: 0,
          features: [],
        };
      }

      stats[key].count++;
      stats[key].totalSize += entry.response.content.size || 0;
    });

    // Calculate averages
    Object.values(stats).forEach(stat => {
      const entries = filteredEntries.filter(e => 
        e.request.httpVersion?.toLowerCase() === stat.protocol
      );
      stat.avgTime = entries.reduce((sum, e) => sum + e.time, 0) / entries.length;
    });

    return Object.values(stats).sort((a, b) => b.count - a.count);
  }, [filteredEntries]);

  const insights = useMemo(() => {
    const result: ProtocolInsight[] = [];

    // Check for HTTP/2 features
    const http2Entries = filteredEntries.filter(e => 
      e.request.httpVersion?.toLowerCase().includes('2')
    );

    if (http2Entries.length > 0) {
      // Check for server push
      const pushEntries = http2Entries.filter(e => {
        // Server push is indicated by specific headers in HTTP/2
        const hasPushHeader = e.response.headers.some(h => 
          h.name.toLowerCase() === 'x-http2-push' || 
          h.name.toLowerCase() === ':authority'
        );
        return hasPushHeader;
      });

      if (pushEntries.length > 0) {
        result.push({
          type: 'info',
          icon: <Zap className="w-4 h-4" />,
          title: 'HTTP/2 Server Push detected',
          description: `${pushEntries.length} resources were server-pushed, reducing round-trip latency.`,
          entries: pushEntries.slice(0, 3),
        });
      }

      // Check for multiplexing (multiple concurrent streams)
      const multiplexedRequests = http2Entries.filter(e => e.time < 100);
      if (multiplexedRequests.length > 10) {
        result.push({
          type: 'success',
          icon: <Layers className="w-4 h-4" />,
          title: 'HTTP/2 Multiplexing active',
          description: `${multiplexedRequests.length} requests were multiplexed over single connection, reducing latency.`,
          entries: multiplexedRequests.slice(0, 3),
        });
      }
    }

    // Check for HTTP/3 (QUIC)
    const http3Entries = filteredEntries.filter(e => 
      e.request.httpVersion?.toLowerCase().includes('3') ||
      e.request.httpVersion?.toLowerCase().includes('quic')
    );

    if (http3Entries.length > 0) {
      result.push({
        type: 'success',
        icon: <Zap className="w-4 h-4" />,
        title: 'HTTP/3 (QUIC) connections',
        description: `${http3Entries.length} requests used HTTP/3 with 0-RTT connection establishment.`,
        entries: http3Entries.slice(0, 3),
      });
    }

    // Check for HTTP/1.x downgrade warnings
    const http1Entries = filteredEntries.filter(e => 
      e.request.httpVersion?.toLowerCase().includes('1.1') ||
      e.request.httpVersion?.toLowerCase().includes('1.0')
    );

    if (http1Entries.length > 0 && http2Entries.length > 0) {
      result.push({
        type: 'warning',
        icon: <AlertTriangle className="w-4 h-4" />,
        title: 'Mixed HTTP versions detected',
        description: `${http1Entries.length} requests still using HTTP/1.x. Consider upgrading all resources to HTTP/2 or HTTP/3 for better performance.`,
        entries: http1Entries.slice(0, 3),
      });
    }

    // Check for TLS 1.3 usage
    const tls13Entries = filteredEntries.filter(e => {
      const tlsVersion = e.response.headers.find(h => 
        h.name.toLowerCase().includes('tls') || 
        h.name.toLowerCase().includes('ssl')
      );
      return tlsVersion?.value?.includes('1.3');
    });

    if (tls13Entries.length > 0) {
      result.push({
        type: 'success',
        icon: <Shield className="w-4 h-4" />,
        title: 'TLS 1.3 in use',
        description: `${tls13Entries.length} connections using TLS 1.3 with improved handshake performance.`,
        entries: tls13Entries.slice(0, 3),
      });
    }

    return result;
  }, [filteredEntries]);

  if (filteredEntries.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <Server className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No data available</h3>
        <p className="text-xs text-slate-500">Load a HAR file to analyze HTTP protocols.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 space-y-4">
        {/* Protocol Distribution */}
        <div className="grid grid-cols-2 gap-3">
          {protocolStats.map(stat => (
            <div
              key={stat.protocol}
              className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={cn(
                  'text-lg font-bold',
                  stat.protocol.includes('2') && 'text-blue-600 dark:text-blue-400',
                  stat.protocol.includes('3') && 'text-purple-600 dark:text-purple-400',
                  (stat.protocol.includes('1.1') || stat.protocol.includes('1.0')) && 'text-slate-600 dark:text-slate-400'
                )}>
                  {stat.protocol.toUpperCase()}
                </span>
                <span className="text-xs text-slate-500">
                  {stat.count} requests
                </span>
              </div>
              <div className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                <div className="flex justify-between">
                  <span>Avg response:</span>
                  <span className="font-medium">{formatTime(stat.avgTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total size:</span>
                  <span className="font-medium">{formatBytes(stat.totalSize)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Insights */}
        {insights.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Protocol Insights
            </h3>
            {insights.map((insight, index) => (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border',
                  insight.type === 'warning' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30',
                  insight.type === 'success' && 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30',
                  insight.type === 'info' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'p-1.5 rounded',
                    insight.type === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                    insight.type === 'success' && 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400',
                    insight.type === 'info' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  )}>
                    {insight.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={cn(
                      'text-sm font-medium',
                      insight.type === 'warning' && 'text-yellow-700 dark:text-yellow-400',
                      insight.type === 'success' && 'text-green-700 dark:text-green-400',
                      insight.type === 'info' && 'text-blue-700 dark:text-blue-400'
                    )}>
                      {insight.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {insight.description}
                    </p>
                    {insight.entries && insight.entries.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {insight.entries.map((entry, i) => (
                          <button
                            key={i}
                            onClick={() => selectEntry(entry)}
                            className="w-full text-left text-xs p-1.5 rounded bg-white/50 dark:bg-slate-900/30 hover:bg-white dark:hover:bg-slate-900/50 transition-colors flex items-center justify-between gap-2"
                          >
                            <span className="truncate text-slate-700 dark:text-slate-300">
                              {getFileName(entry.request.url)}
                            </span>
                            <span className="flex-shrink-0 text-[10px] text-slate-500">
                              {entry.request.httpVersion}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Protocol Features Info */}
        <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Protocol Features
          </h4>
          <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
            <li className="flex items-start gap-2">
              <ArrowRight className="w-3 h-3 mt-0.5 text-blue-500 flex-shrink-0" />
              <span><strong>HTTP/2:</strong> Multiplexing, server push, header compression (HPACK)</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-3 h-3 mt-0.5 text-purple-500 flex-shrink-0" />
              <span><strong>HTTP/3:</strong> QUIC protocol, 0-RTT, improved congestion control</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-3 h-3 mt-0.5 text-slate-500 flex-shrink-0" />
              <span><strong>HTTP/1.1:</strong> Persistent connections, chunked encoding</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
